import {
    IDriveThruCarEvent,
    IDriveThruTransaction,
    IDriveThruReplayResponse,
    IDriveThruReplayExperience,
    BottleneckStation,
    CascadeInfo
} from "./driveThruReplayModels";
import {IConceptDriveThruThresholdsConfig} from "@/features/coachInsights/api/coachInsightsConceptConfig";

type IndexedExperience = IDriveThruReplayExperience & {eventId: string};

export interface IMappedReplayData {
    events: IDriveThruCarEvent[];
    transactions: Record<string, IDriveThruTransaction>;
    cascadeMap: Map<string, CascadeInfo>;
    thresholdSeconds: number;
}

// Groups cascade experiences by cascadeGroupId and derives each car's role, index within the group, and the group's bottleneck station.
function buildCascadeMap(
    replayTransactions: IndexedExperience[]
): Map<string, CascadeInfo> {
    const cascadeMap = new Map<string, CascadeInfo>();

    const groups = new Map<number, IndexedExperience[]>();
    for (const t of replayTransactions) {
        if (t.cascadeGroupId === 0) continue;
        const existing = groups.get(t.cascadeGroupId) ?? [];
        existing.push(t);
        groups.set(t.cascadeGroupId, existing);
    }

    for (const [, members] of groups) {
        const sorted = [...members].sort((a, b) => a.secondOfDay - b.secondOfDay);
        const cascadeLength = sorted.length;

        const avgMenu =
            sorted.reduce((s, m) => s + (m.menuSec ?? 0), 0) / sorted.length;
        const avgQueue =
            sorted.reduce((s, m) => s + (m.queueSec ?? 0), 0) / sorted.length;
        const avgWindow =
            sorted.reduce((s, m) => s + (m.windowSec ?? 0), 0) / sorted.length;
        const maxAvg = Math.max(avgMenu, avgQueue, avgWindow);
        const bottleneckStation =
            maxAvg === 0
                ? null
                : maxAvg === avgMenu
                ? BottleneckStation.Menu
                : maxAvg === avgQueue
                ? BottleneckStation.Queue
                : BottleneckStation.Window;

        sorted.forEach((member, posIdx) => {
            cascadeMap.set(member.eventId, {
                category: member.cascadeTrigger,
                positionIndex: posIdx,
                groupId: member.cascadeGroupId,
                bottleneckStation,
                cascadeLength
            });
        });
    }

    return cascadeMap;
}

// Converts the API response into the data the UI needs: a list of car events, a lookup of transactions by id, and cascade info per car.
export function mapReplayDataToEvents(
    response: IDriveThruReplayResponse,
    conceptConfig: IConceptDriveThruThresholdsConfig
): IMappedReplayData {
    const events: IDriveThruCarEvent[] = [];
    const transactions: Record<string, IDriveThruTransaction> = {};

    const indexedExperiences: IndexedExperience[] = response.experiences.map(
        (t, idx) => ({
            ...t,
            eventId: `${t.secondOfDay}_${idx}`
        })
    );

    indexedExperiences.forEach((t, idx) => {
        const id = t.eventId;

        const event: IDriveThruCarEvent = {
            id,
            secondOfDay: t.secondOfDay,
            orderStationSeconds: t.menuSec ?? null,
            paymentWindowSeconds: t.queueSec ?? null,
            presentationWindowSeconds: t.windowSec ?? null,
            totalServiceSeconds: t.totalSec ?? 0,
            isOverThreshold: (t.totalSec ?? 0) > conceptConfig.thresholdSeconds,
            vehicleNumber: idx + 1,
            transactionId: id,
            cashierName: t.empName ?? "Unknown"
        };

        events.push(event);

        const tx: IDriveThruTransaction = {
            transactionId: id,
            orderNumber: t.orderNumber ?? "",
            ticket: t.ticket ?? null
        };

        transactions[id] = tx;
    });

    const cascadeMap = buildCascadeMap(indexedExperiences);

    return {
        events,
        transactions,
        cascadeMap,
        thresholdSeconds: conceptConfig.thresholdSeconds
    };
}
